import React from 'react';

import {
  EntertainmentPlayerName,
  type EntertainmentPlayerIdentity,
} from '@/components/entertainment/EntertainmentPlayerName';
import { Button, Card } from '@/components/ui';
import type { EntertainmentLedgerEntry, LedgerDraftChange } from '@/lib/entertainment/ledger-types';

type Props = {
  entries: EntertainmentLedgerEntry[];
  users?: EntertainmentPlayerIdentity[];
  onApply: (changes: LedgerDraftChange[]) => Promise<void> | void;
};

function centsFromInput(value: string): number {
  return Math.round(Number(value) * 100);
}

function formatDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

function transactionTotals(entries: EntertainmentLedgerEntry[], draftAmounts: Record<string, number>) {
  const totals = new Map<string, number>();
  for (const entry of entries) {
    const amount = draftAmounts[entry.id] ?? entry.amount_cents;
    totals.set(entry.transaction_id, (totals.get(entry.transaction_id) ?? 0) + amount);
  }
  return totals;
}

export function AdminLedgerWorkspace({ entries, users = [], onApply }: Props) {
  const [draftAmounts, setDraftAmounts] = React.useState<Record<string, number>>({});
  const [reviewing, setReviewing] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const userById = React.useMemo(() => new Map(users.map((user) => [user.id, user])), [users]);

  const changes = React.useMemo(
    () => Object.entries(draftAmounts).map(([entryId, amount_cents]) => ({ entryId, amount_cents })),
    [draftAmounts],
  );
  const impactedTransactionIds = React.useMemo(() => {
    const impacted = new Set<string>();
    for (const entry of entries) {
      if (Object.prototype.hasOwnProperty.call(draftAmounts, entry.id)) {
        impacted.add(entry.transaction_id);
      }
    }
    return impacted;
  }, [draftAmounts, entries]);
  const unbalancedTransactions = React.useMemo(() => {
    const totals = transactionTotals(entries, draftAmounts);
    return Array.from(totals.entries())
      .filter(([transactionId, total]) => impactedTransactionIds.has(transactionId) && total !== 0)
      .map(([transactionId]) => transactionId);
  }, [draftAmounts, entries, impactedTransactionIds]);
  const canApply = changes.length > 0 && unbalancedTransactions.length === 0 && !applying;

  const apply = async () => {
    if (!canApply) return;
    setApplying(true);
    setMessage(null);
    try {
      await onApply(changes);
      setDraftAmounts({});
      setReviewing(false);
      setMessage('Draft applied.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <h2 className="text-sm font-bold text-text-muted mb-3 uppercase tracking-widest">
          Ledger
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-left text-sm">
            <thead className="text-[10px] font-bold uppercase tracking-widest text-text-muted">
              <tr>
                <th className="w-40 py-2">User</th>
                <th className="w-36 py-2">Kind</th>
                <th className="w-32 py-2">Amount</th>
                <th className="w-24 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-t border-border-subtle">
                  <td className="py-2 pr-3">
                    <EntertainmentPlayerName
                      user={userById.get(entry.user_id)}
                      fallbackId={entry.user_id}
                    />
                  </td>
                  <td className="py-2 pr-3 text-xs font-semibold text-text-muted">{entry.entry_kind}</td>
                  <td className="py-2 pr-3">
                    <input
                      aria-label={`${entry.id} amount`}
                      type="number"
                      step="0.01"
                      value={formatDollars(draftAmounts[entry.id] ?? entry.amount_cents)}
                      onChange={(event) => {
                        const nextAmount = centsFromInput(event.target.value);
                        setDraftAmounts((previous) => ({
                          ...previous,
                          [entry.id]: Number.isFinite(nextAmount) ? nextAmount : entry.amount_cents,
                        }));
                      }}
                      className="h-9 w-28 rounded-lg border border-border-subtle bg-surface px-2 text-sm font-semibold text-text-main focus:border-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30"
                    />
                  </td>
                  <td className="py-2 text-xs font-bold text-text-muted">{entry.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button type="button" variant="secondary" size="sm" disabled={changes.length === 0} onClick={() => setReviewing(true)}>
            Review Changes
          </Button>
          {message && <div className="text-sm font-semibold text-brand-primary">{message}</div>}
        </div>

        {reviewing && (
          <div className="rounded-lg border border-border-subtle bg-bg-app p-4">
            <h3 className="text-sm font-bold text-text-main">Draft Changes</h3>
            <div className="mt-1 text-xs font-semibold text-text-muted">
              {changes.length} change(s) ready for review.
            </div>
            {unbalancedTransactions.length > 0 && (
              <div className="mt-3 text-sm font-bold text-status-error">
                Changes must keep each transaction at zero.
              </div>
            )}
            <Button type="button" size="sm" className="mt-3" disabled={!canApply} onClick={apply}>
              Apply Draft
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
